import { useState } from 'react';
import { message } from 'antd';
import { useAPIContractService } from 'services/contracts/contracts.service';
import { ExtractedContractDto } from 'services/contracts/extracted-contract.dto';
import { ImportContractsFragmentProps, ImportContractsController } from './interfaces';

export const useImportContractsController = (props: ImportContractsFragmentProps): ImportContractsController => {
    const service = useAPIContractService();
    const [fileList, setFileList] = useState<File[]>([]);
    const [results, setResults] = useState<ExtractedContractDto[]>([]);
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionDone, setExtractionDone] = useState(false);

    const onFilesChange = (files: File[]) => {
        setFileList(files);
        setResults([]);
        setExtractionDone(false);
    };

    const onExtract = async () => {
        if (fileList.length === 0) {
            message.warning('Seleccioná al menos un archivo');
            return;
        }
        setIsExtracting(true);
        const extracted: ExtractedContractDto[] = [];
        for (const file of fileList) {
            try {
                const result = await service.extractFromFile(file);
                extracted.push(result);
            } catch (error: any) {
                console.error(`Error processing file ${file.name}:`, error);
                let errorMessage = 'Error de conexión';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.status === 413) {
                    errorMessage = 'El archivo es demasiado grande';
                } else if (error.response?.status === 415) {
                    errorMessage = 'Formato de archivo no soportado';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                message.error(`Error al procesar ${file.name}: ${errorMessage}`);
                extracted.push({
                    fileName: file.name,
                    fileId: 0,
                    property: { street: null, number: null, floor: null, city: null, district: null, existingId: null },
                    owner: { name: null, lastName: null, dni: null, phone: null, email: null, existingId: null },
                    renter: { name: null, lastName: null, dni: null, phone: null, email: null, existingId: null },
                    guarantor: null,
                    contract: {
                        startPrice: null,
                        initialDate: null,
                        durationMonths: null,
                        finishDate: null,
                        updateFrequency: null,
                        percentageByAdministration: null,
                        expiredDay: null,
                    },
                    warnings: [`No se pudo procesar el archivo: ${errorMessage}`],
                });
            }
        }
        setResults(extracted);
        setIsExtracting(false);
        setExtractionDone(true);
    };

    const onConfirm = () => {
        props.onImported(results);
        setFileList([]);
        setResults([]);
        setExtractionDone(false);
    };

    const onCancel = () => {
        props.onCancel();
        setFileList([]);
        setResults([]);
        setExtractionDone(false);
    };

    return {
        fileList,
        results,
        isExtracting,
        extractionDone,
        onFilesChange,
        onExtract,
        onConfirm,
        onCancel,
    };
};
